<script setup lang="ts">
import type { Price } from '@optionway/models';

const properties = withDefaults(defineProps<Price & { dark?: boolean }>(), {
  dark: false,
});

const prefix = 'components.results.priceSelect.tooltip';

const { t } = useI18n();

const paxGroups = computed(() =>
  [
    { group: properties.adultGroup, label: t(`${prefix}.adults`) },
    { group: properties.childGroup, label: t(`${prefix}.children`) },
    { group: properties.infantGroup, label: t(`${prefix}.infants`) },
  ].filter((item) => Boolean(item.group)),
);
</script>

<template>
  <div class="text-primary-900">
    <!-- Price details per group -->
    <template
      v-for="{ group, label } in paxGroups"
      :key="label"
    >
      <div class="mb-1 mt-2 flex justify-between text-xs leading-5">
        <div class="font-semibold">{{ label }}</div>
        <div>{{ group?.count }} x {{ usePriceFormat(group?.paxDetail.total) }}</div>
      </div>

      <div class="flex justify-between text-xs leading-6">
        <div :class="{ 'text-text-inverse': dark, 'text-text-secondary-600': !dark }">{{ $t(`${prefix}.base`) }}</div>
        <div>{{ group?.count }} x {{ usePriceFormat(group?.paxDetail.base) }}</div>
      </div>

      <div class="flex justify-between text-xs leading-6">
        <div :class="{ 'text-text-inverse': dark, 'text-text-secondary-600': !dark }">{{ $t(`${prefix}.taxes`) }}</div>
        <div>{{ group?.count }} x {{ usePriceFormat(group?.paxDetail.taxes) }}</div>
      </div>

      <div class="flex justify-between text-xs leading-6">
        <div :class="{ 'text-text-inverse': dark, 'text-text-secondary-600': !dark }">{{ $t(`${prefix}.fees`) }}</div>
        <div>{{ group?.count }} x {{ usePriceFormat(group?.paxDetail.fees) }}</div>
      </div>

      <div class="mb-3 mt-3 h-px w-full border-t border-gray-300" />
    </template>

    <!-- Payment fees -->
    <div class="mb-2 flex justify-between text-xs font-semibold">
      <div>{{ $t(`${prefix}.paymentFees`) }}</div>
      <div>{{ $t(`${prefix}.paymentFeesFree`) }}</div>
    </div>

    <div class="mb-3 mt-3 h-px w-full border-t border-gray-300" />

    <!-- Grand total -->
    <div
      class="flex justify-between text-sm font-bold leading-6"
      :class="{ 'text-brand-primary-400': dark, 'text-brand-primary-700': !dark }"
    >
      <div>{{ $t(`${prefix}.grandTotal`) }}</div>
      <div>{{ usePriceFormat(total) }}</div>
    </div>
  </div>
</template>
